// Admin pages (admin PR 3a): pure texts and helpers, tested in test/admin.test.ts.

import type { AdminReadiness, TimelineCategory, TimelineEntry } from '../api';
import { clockTime, daysAgo, momentText, shortDate, shownZone } from '../time';
import { nicknameOf } from '../phone/number-names';

export type Tone = 'ok' | 'warn' | 'bad' | 'idle';

/** The chip next to a user's name: what an admin should notice first. */
export function statusChip(
  u: { status: 'active' | 'disabled'; mustSetPassword?: boolean; tempPasswordUntil?: string | null; invited?: boolean; inviteUntil?: string | null },
  nowMs: number,
): { label: string; tone: Tone } {
  if (u.status === 'disabled') return { label: 'Deactivated', tone: 'bad' };
  if (u.invited) {
    // admin PR 3b: waiting for the person to open the invitation email
    const until = u.inviteUntil ? Date.parse(u.inviteUntil) : NaN;
    if (Number.isFinite(until) && until <= nowMs) return { label: 'Invitation expired', tone: 'bad' };
    return { label: 'Invited', tone: 'warn' };
  }
  if (u.mustSetPassword) {
    const until = u.tempPasswordUntil ? Date.parse(u.tempPasswordUntil) : NaN;
    if (Number.isFinite(until) && until <= nowMs) return { label: 'Temporary password expired', tone: 'bad' };
    return { label: 'Must set password', tone: 'warn' };
  }
  return { label: 'Active', tone: 'ok' };
}

/** "Never logged in", "Active today", "Yesterday", "5 days ago", "Oct 3". */
export function lastLoginText(iso: string | null, nowMs: number): string {
  const t = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(t)) return 'Never logged in';
  // calendar days in the viewer's time zone (time-zone PR)
  const days = daysAgo(t, nowMs);
  if (days <= 0) return 'Logged in today';
  if (days === 1) return 'Logged in yesterday';
  if (days < 7) return `Logged in ${days} days ago`;
  return `Last login ${dateText(t, nowMs)}`;
}

/** "Oct 3" this year, "Oct 3, 2025" otherwise (the viewer's time zone). */
export function dateText(t: number, nowMs: number): string {
  return shortDate(t, nowMs);
}

/**
 * "Oct 28, 4:30 AM EDT" (the viewer's time zone, with its label; "Karachi time" where
 * there is no short name): when a link or temporary password stops working.
 */
export function untilText(iso: string, nowMs: number = Date.now()): string {
  return momentText(iso, nowMs);
}

/**
 * Timeline lines come from the server with times in UTC ("until 2026-11-03 15:58 UTC");
 * each viewer sees them in their own time zone ("until Nov 3, 11:58 AM EDT"). The UTC
 * original stays in the line's tooltip.
 */
export function localTimes(text: string, nowMs: number = Date.now()): string {
  return text.replace(/\b(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}) UTC\b/g, (m, y: string, mo: string, d: string, h: string, mi: string) => {
    const t = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi));
    return Number.isFinite(t) ? untilText(new Date(t).toISOString(), nowMs) : m;
  });
}

/** "Updated just now" / "Updated 3 min ago" (the auto-refresh line). */
export function updatedText(at: number, nowMs: number): string {
  const min = Math.floor((nowMs - at) / 60_000);
  if (min < 1) return 'Updated just now';
  if (min < 60) return `Updated ${min} min ago`;
  return `Updated ${Math.floor(min / 60)} h ago`;
}

/** Timeline day headings: "Today", "Yesterday", "Friday, Oct 23" (this year), "Oct 23, 2025". */
export function dayHeading(iso: string, nowMs: number): string {
  const t = Date.parse(iso);
  const zone = shownZone();
  const days = daysAgo(t, nowMs, zone);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  const year = (x: number) => new Intl.DateTimeFormat('en-US', { timeZone: zone, year: 'numeric' }).format(x);
  if (year(t) === year(nowMs)) return new Intl.DateTimeFormat('en-US', { timeZone: zone, weekday: 'long', month: 'short', day: 'numeric' }).format(t);
  return new Intl.DateTimeFormat('en-US', { timeZone: zone, month: 'short', day: 'numeric', year: 'numeric' }).format(t);
}

export const timeText = (iso: string) => clockTime(iso, 'en-US');

/** Who did it, as the timeline shows it (empty: the user on the login / reset pages). */
export function actorLabel(a: TimelineEntry['actor']): string {
  switch (a.kind) {
    case 'user':
      return 'by the user';
    case 'admin':
      return a.email ? `by ${a.email}` : 'by an admin';
    case 'cli':
      return 'on the server';
    case 'system':
      return 'by the system';
    default:
      return '';
  }
}

export const CATEGORY_LABELS: Record<TimelineCategory, string> = {
  account: 'Account',
  security: 'Security',
  login: 'Logins',
  settings: 'Settings',
  phone: 'Phone',
  calls: 'Calls',
  numbers: 'Numbers',
  plan: 'Plan & credit',
  note: 'Notes',
  other: 'Other',
};
/** The filter chips, in order (null = everything). */
export const TIMELINE_FILTERS: Array<TimelineCategory | null> = [null, 'account', 'security', 'login', 'settings', 'phone', 'calls', 'numbers', 'plan', 'note'];

export type TimelineRow =
  | { kind: 'entry'; entry: TimelineEntry }
  /** successful logins in a row from the same device, folded into one line */
  | { kind: 'logins'; entries: TimelineEntry[]; device: string; key: string };

/**
 * Successful logins one after another (nothing else in between) from the same device
 * fold into "Logged in 6 times on Chrome on Windows". Two or more fold; one stays as it is.
 */
export function foldLogins(entries: TimelineEntry[]): TimelineRow[] {
  const out: TimelineRow[] = [];
  let run: TimelineEntry[] = [];
  const flush = () => {
    if (run.length >= 2) out.push({ kind: 'logins', entries: run, device: run[0]!.device ?? '', key: `logins-${run[0]!.id}` });
    else for (const e of run) out.push({ kind: 'entry', entry: e });
    run = [];
  };
  for (const e of entries) {
    const isLogin = e.action === 'login.ok' && e.actor.kind === 'user';
    if (isLogin && (run.length === 0 || (run[0]!.device ?? '') === (e.device ?? ''))) {
      run.push(e);
      continue;
    }
    flush();
    if (isLogin) run.push(e);
    else out.push({ kind: 'entry', entry: e });
  }
  flush();
  return out;
}

export function loginsTitle(n: number, device: string): string {
  return `Logged in ${n} times${device ? ` on ${device}` : ''}`;
}

/** Why an invitation email didn't go out, in an admin's words. */
export function inviteMailText(mail: string): string {
  return mail === 'limit'
    ? 'the hourly email limit was reached'
    : mail === 'off'
      ? 'email isn’t set up on this server'
      : 'the mail server refused it';
}

/** The invitation line on the user page (admin PR 3b). */
export function inviteLine(
  inv: { state: 'none' | 'open' | 'expired'; sentAt: string | null; until: string | null; sentBy: string | null; acceptedAt: string | null; endedWhy: string | null } | null | undefined,
  nowMs: number,
): string | null {
  if (!inv) return null;
  const sent = inv.sentAt ? `${dateText(Date.parse(inv.sentAt), nowMs)}${inv.sentBy ? ` by ${inv.sentBy}` : ''}` : '';
  if (inv.state === 'open') return `Invited ${sent}. The link works until ${untilText(inv.until!)}.`;
  if (inv.state === 'expired') return `Invited ${sent}. The link expired ${untilText(inv.until!)}.`;
  if (inv.acceptedAt) return `Joined from an invitation on ${dateText(Date.parse(inv.acceptedAt), nowMs)}.`;
  const why: Record<string, string> = {
    cancelled: 'was cancelled',
    email_changed: 'stopped working when the login email changed',
    deactivated: 'stopped working when the account was deactivated',
    temp_password: 'was replaced by a temporary password',
    password_set: 'stopped working when a password was set',
    resent: 'was replaced by a newer one',
  };
  return `The last invitation (${sent}) ${why[inv.endedWhy ?? ''] ?? 'stopped working'}.`;
}

/** Errors of the admin API in the words an admin needs. */
export function adminErrorText(err: unknown): string {
  const e = err as { code?: string; status?: number; message?: string };
  if (e?.code === 'not_allowed_here') return 'Admin tools only work from your VPN. Connect it and reload this page.';
  if (e?.code === 'network') return 'Can’t reach CloudPhone. Check your internet connection.';
  if (e?.status === 401) return 'Your admin login ended. Log in again.';
  return callEngine(e?.message || 'Something went wrong. Try again.');
}

/** Admins see the billing / call system as "Call Engine" (owner, session 21), also in server texts. */
export function callEngine(text: string): string {
  return text
    .replace(/\bthe MagnusBilling panel\b/gi, 'the Call Engine')
    .replace(/\bMagnus\s?Billing\b(’s|'s)?/gi, (_m, poss: string | undefined) => (poss ? 'Call Engine’s' : 'Call Engine'))
    .replace(/\bMB panel\b/g, 'Call Engine')
    .replace(/\bMagnus\b/gi, 'Call Engine');
}

/** "+1 (201) 285-8570" for US / Canada numbers, digits otherwise. */
/** Numbers PR 2: "+1 (201) 285-8570 · Sales" (admins think in numbers, so the number first). */
export function adminNumberLabel(n: string, names: Readonly<Record<string, string>> | null | undefined): string {
  const name = nicknameOf(names, n);
  return name ? `${numberText(n)} · ${name}` : numberText(n);
}

export function numberText(n: string): string {
  const d = n.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return n;
}

/** Initials for the avatar: "Alice Tester" → "AT", "bob@x.com" → "B". */
export function initials(name: string | null, email: string): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
  if (words.length === 1) return words[0]![0]!.toUpperCase();
  return (email[0] ?? '?').toUpperCase();
}

export const ADMIN_PATH = '/admin';
export type AdminPage = 'users' | 'plans' | 'settings';
/** `/admin` → list, `/admin/users/12` → that user, `/admin/plans`, `/admin/settings` (PR 1a). */
export function parseAdminPath(path: string): { page: AdminPage; userId: number | null } | null {
  if (path === ADMIN_PATH || path === `${ADMIN_PATH}/`) return { page: 'users', userId: null };
  const m = /^\/admin\/users\/(\d{1,18})\/?$/.exec(path);
  if (m) return { page: 'users', userId: Number(m[1]) };
  const p = /^\/admin\/(plans|settings)\/?$/.exec(path);
  return p ? { page: p[1] as AdminPage, userId: null } : null;
}
export const pagePath = (page: AdminPage) => (page === 'users' ? ADMIN_PATH : `${ADMIN_PATH}/${page}`);
export const userPath = (id: number) => `${ADMIN_PATH}/users/${id}`;

// ── Readiness (PR 1b) ──


const cents = (c: number) => `${c < 0 ? '−' : ''}$${Math.floor(Math.abs(c) / 100)}.${String(Math.abs(c) % 100).padStart(2, '0')}`;

/** The list chip for an account that can't call fully (null = ready, no phone line, or not read yet). */
export function readinessChip(r: AdminReadiness | undefined, active: boolean): { label: string; tone: Tone } | null {
  if (!r || !active || !r.state || r.state === 'ready') return null;
  if (r.state === 'no_number') return { label: 'No number', tone: 'warn' };
  if (r.state === 'no_credit') return r.freeMinutes ? { label: 'No credit · free minutes', tone: 'warn' } : { label: 'No credit', tone: 'bad' };
  return { label: 'Low credit', tone: 'warn' };
}

export interface ReadinessView {
  headline: string;
  tone: Tone;
  facts: Array<{ label: string; value: string }>;
  /** what's wrong and what to do, in order */
  todo: Array<{ tone: Tone; text: string }>;
}

/** The user page's Readiness box. `minutesAgo` = since the worker last read the Call Engine. */
export function readinessView(r: AdminReadiness, lowCents: number, minutesAgo: number | null): ReadinessView {
  const todo: ReadinessView['todo'] = [];
  if (!r.readAt || !r.state) {
    return { headline: 'Not read yet', tone: 'idle', facts: [], todo: [{ tone: 'idle', text: 'The app reads the Call Engine every minute. Refresh in a moment.' }] };
  }
  const headline =
    r.state === 'ready' ? 'Ready to call' : r.state === 'no_number' ? 'No number' : r.state === 'no_credit' ? 'No credit' : 'Low credit';
  const tone: Tone = r.state === 'ready' ? 'ok' : r.state === 'no_credit' && !r.freeMinutes ? 'bad' : 'warn';
  const facts = [
    { label: 'Credit', value: `${cents(r.credit ?? 0)}${r.postpaid ? ` (postpaid, limit $${r.creditLimit ?? 0})` : ' (prepaid)'}` },
    // PR 2: "free minutes" = an offer with minutes left (used up → No; the Usage box has the details)
    { label: 'Free minutes', value: r.freeMinutes ? 'Yes, minutes left (a Call Engine offer)' : 'No' },
    { label: 'Last read', value: minutesAgo === null ? '—' : minutesAgo < 1 ? 'just now' : `${minutesAgo} min ago` },
  ];
  if (r.state === 'no_number') todo.push({ tone: 'warn', text: 'Assign a number in the Call Engine → DIDs. The app picks it up within a minute and emails the user.' });
  if (r.state === 'no_credit') {
    todo.push(
      r.freeMinutes
        ? { tone: 'warn', text: 'No credit, but the Call Engine offer still has free minutes, so outgoing calls work until they run out. The user isn’t told.' }
        : { tone: 'bad', text: 'Calls can’t go out. Add credit in the Call Engine (Users → Refill). The user is told their balance is empty.' },
    );
  }
  if (r.state === 'low_credit') {
    todo.push({
      tone: 'warn',
      text: `Below the low-credit amount (${cents(lowCents)}, Admin → Settings).${r.freeMinutes ? ' The user isn’t told: they have free minutes.' : ' The user sees “Your balance is low”.'}`,
    });
  }
  if (r.noPlan) todo.push({ tone: 'warn', text: 'No plan yet: assign one under Plan.' });
  if (r.postpaid || (r.creditLimit ?? 0) !== 0) {
    todo.push({ tone: 'bad', text: 'Postpaid or a credit limit in the Call Engine. CloudPhone is prepaid only: set it to prepaid with credit limit 0.' });
  }
  if (r.mbReminderOn) {
    todo.push({ tone: 'warn', text: 'The Call Engine’s own low-credit email is on (it emails hourly, in its own words). Set credit notification to −1 (Users → Customers).' });
  }
  if (minutesAgo !== null && minutesAgo >= 5) todo.push({ tone: 'warn', text: `Last read ${minutesAgo} min ago: the Call Engine may be unreachable. These values may be old.` });
  return { headline, tone, facts, todo };
}
